import * as THREE from 'three';
import { applyTransmissionAttenuation, sanitizeAttenuationDistance } from './transmission-attenuation';

/**
 * Stable yaw for "link env to camera" — orbit azimuth on the XZ plane from the
 * orbit target (or world origin). Avoids Spherical.theta flips when pitching
 * below the horizon (negative vertical angles).
 */
export function cameraLinkedEnvYaw(
  camera: THREE.Camera,
  orbitTarget?: THREE.Vector3 | null,
): number {
  const x = camera.position.x - (orbitTarget?.x ?? 0);
  const z = camera.position.z - (orbitTarget?.z ?? 0);
  if (x * x + z * z < 1e-12) return 0;
  // Match THREE.Spherical theta convention (from +Z toward +X).
  return Math.atan2(x, z);
}

/** Resolve OrbitControls / CameraControls target when present on the R3F root. */
export function getOrbitTarget(controls: unknown): THREE.Vector3 | null {
  if (!controls || typeof controls !== 'object') return null;
  const t = (controls as { target?: unknown }).target;
  if (t instanceof THREE.Vector3) return t;
  return null;
}

function boxVolume(box: THREE.Box3): number {
  if (box.isEmpty()) return 0;
  const sx = Math.max(0, box.max.x - box.min.x);
  const sy = Math.max(0, box.max.y - box.min.y);
  const sz = Math.max(0, box.max.z - box.min.z);
  return sx * sy * sz;
}

function boxMostlyInside(inner: THREE.Box3, outer: THREE.Box3, fraction: number): boolean {
  if (inner.isEmpty() || outer.isEmpty()) return false;
  const ix0 = Math.max(inner.min.x, outer.min.x);
  const iy0 = Math.max(inner.min.y, outer.min.y);
  const iz0 = Math.max(inner.min.z, outer.min.z);
  const ix1 = Math.min(inner.max.x, outer.max.x);
  const iy1 = Math.min(inner.max.y, outer.max.y);
  const iz1 = Math.min(inner.max.z, outer.max.z);
  if (ix1 <= ix0 || iy1 <= iy0 || iz1 <= iz0) return false;
  const overlap = (ix1 - ix0) * (iy1 - iy0) * (iz1 - iz0);
  const innerVol = Math.max(1e-12, boxVolume(inner));
  return overlap / innerVol >= fraction;
}

function boxesNearOrIntersect(a: THREE.Box3, b: THREE.Box3, margin: number): boolean {
  if (a.isEmpty() || b.isEmpty()) return false;
  if (a.intersectsBox(b)) return true;
  if (margin <= 0) return false;
  const expanded = a.clone().expandByScalar(margin);
  return expanded.intersectsBox(b);
}

function nameHintsLiquid(name: string): boolean {
  return /liquid|cognac|wine|fill|fluid|content|eau|spirit/i.test(name);
}

function nameHintsGlass(name: string): boolean {
  // Do not match "Decanter Metal …" / "Decanter - Liquid" — those are cage/liquid.
  if (/metal|liquid|cognac|fill|fluid|content/i.test(name)) return false;
  return /glass|crystal|verre|bottle.?glass|neck.?glass/i.test(name);
}

/**
 * Opaque wet liquid — used when liquid is fully enclosed by a larger glass shell
 * (classic bottle). Clearcoat approximates the lost inner reflection.
 */
function applyWetOpaqueLiquid(mat: THREE.MeshPhysicalMaterial, priorTransmission: number): void {
  mat.transmission = 0;
  mat.transparent = false;
  mat.depthWrite = true;
  mat.opacity = 1;
  mat.side = THREE.FrontSide;
  const wet = THREE.MathUtils.clamp(0.35 + priorTransmission * 0.45, 0.45, 0.95);
  mat.clearcoat = Math.max(mat.clearcoat ?? 0, wet);
  mat.clearcoatRoughness = Math.min(mat.clearcoatRoughness ?? 0.2, 0.12);
  mat.envMapIntensity = Math.max(mat.envMapIntensity ?? 1, 1);
  if (typeof mat.specularIntensity === 'number') {
    mat.specularIntensity = Math.max(mat.specularIntensity, 1);
  }
  mat.thickness = 0;
  mat.attenuationDistance = Infinity;
  if (mat.attenuationColor) mat.attenuationColor.set(0xffffff);
  mat.needsUpdate = true;
}

/**
 * Neck / tip glass without transmission — keeps a glassy clearcoat read while the
 * larger liquid volume keeps Transmit (Paradis-style separate neck mesh).
 */
function applyClearcoatGlassShell(mat: THREE.MeshPhysicalMaterial, priorTransmission: number): void {
  mat.transmission = 0;
  mat.transparent = false;
  mat.depthWrite = true;
  mat.opacity = 1;
  mat.side = THREE.FrontSide;
  mat.metalness = 0;
  mat.roughness = Math.min(mat.roughness ?? 0.05, 0.06);
  const coat = THREE.MathUtils.clamp(0.75 + priorTransmission * 0.2, 0.8, 1);
  mat.clearcoat = Math.max(mat.clearcoat ?? 0, coat);
  mat.clearcoatRoughness = Math.min(mat.clearcoatRoughness ?? 0.08, 0.04);
  mat.envMapIntensity = Math.max(mat.envMapIntensity ?? 1, 1.15);
  if (typeof mat.specularIntensity === 'number') {
    mat.specularIntensity = Math.max(mat.specularIntensity, 1);
  }
  mat.thickness = 0;
  mat.attenuationDistance = Infinity;
  if (mat.attenuationColor) mat.attenuationColor.set(0xffffff);
  mat.needsUpdate = true;
}

function softenTransmittingGlass(mat: THREE.MeshPhysicalMaterial): void {
  if (typeof mat.specularIntensity === 'number') {
    mat.specularIntensity = Math.min(mat.specularIntensity, 0.55);
  }
  mat.envMapIntensity = Math.min(mat.envMapIntensity ?? 1, 0.85);
  if ((mat.thickness ?? 0) > 0.25) mat.thickness = 0.25;
  // Don't wipe an authored Absorb Color / Dist — only soften clear glass leftovers.
  const dist = mat.attenuationDistance;
  const hasAbsorb =
    Number.isFinite(dist) &&
    dist > 0 &&
    dist < Infinity &&
    (mat.attenuationColor.r < 0.98 || mat.attenuationColor.g < 0.98 || mat.attenuationColor.b < 0.98);
  if (!hasAbsorb) {
    applyTransmissionAttenuation(mat, { clearGlass: true });
  } else {
    // Guard against Dist = 0 leaking onto the GPU material.
    mat.attenuationDistance = sanitizeAttenuationDistance(dist);
  }
  if ((mat.opacity ?? 1) < 0.999) {
    mat.opacity = 1;
    mat.transparent = false;
    mat.depthWrite = true;
  }
  mat.needsUpdate = true;
}

type TxEntry = {
  mesh: THREE.Mesh;
  mat: THREE.MeshPhysicalMaterial;
  box: THREE.Box3;
  size: number;
  vol: number;
  name: string;
};

function classifyPair(a: TxEntry, b: TxEntry): { liquid: TxEntry; glass: TxEntry } {
  const aLiq = nameHintsLiquid(a.name) || nameHintsLiquid(a.mesh.parent?.name || '');
  const bLiq = nameHintsLiquid(b.name) || nameHintsLiquid(b.mesh.parent?.name || '');
  const aGlass = nameHintsGlass(a.name) || nameHintsGlass(a.mesh.parent?.name || '');
  const bGlass = nameHintsGlass(b.name) || nameHintsGlass(b.mesh.parent?.name || '');

  if ((aLiq && bGlass) || (bGlass && !aGlass && a.vol >= b.vol)) return { liquid: a, glass: b };
  if ((bLiq && aGlass) || (aGlass && !bGlass && b.vol >= a.vol)) return { liquid: b, glass: a };
  if (a.vol >= b.vol) return { liquid: a, glass: b };
  return { liquid: b, glass: a };
}

/**
 * True when glass is a small neck/top relative to the liquid body.
 * If liquid sits inside the glass AABB, it is a full shell — never neck-style
 * (tight liquid fits were wrongly demoting the outer glass to opaque clearcoat).
 */
function isNeckStyleGlass(liquid: TxEntry, glass: TxEntry): boolean {
  if (glass.box.containsBox(liquid.box) || boxMostlyInside(liquid.box, glass.box, 0.65)) {
    return false;
  }
  // Glass clearly inside/near a larger liquid body (separate neck mesh).
  if (liquid.box.containsBox(glass.box) || boxMostlyInside(glass.box, liquid.box, 0.65)) {
    return true;
  }
  return glass.size < liquid.size * 0.5 || glass.vol < liquid.vol * 0.25;
}

function markDemoted(mesh: THREE.Mesh, demoted: Set<string>): void {
  const key = mesh.name || mesh.uuid;
  demoted.add(key);
  mesh.userData.c3dNestedTransmissionDemoted = true;
}

/**
 * Keep liquid Transmit; turn overlapping neck glass into clearcoat-only shell.
 */
function keepLiquidDemoteGlass(liquid: TxEntry, glass: TxEntry, demoted: Set<string>): void {
  if (demoted.has(glass.mesh.name || glass.mesh.uuid)) return;
  applyClearcoatGlassShell(glass.mat, glass.mat.transmission);
  markDemoted(glass.mesh, demoted);
  glass.mesh.userData.c3dNestedGlassOuter = false;
  liquid.mesh.userData.c3dNestedTransmissionDemoted = false;
  liquid.mesh.userData.c3dNestedGlassOuter = false;
  // Liquid draws in the transmission buffer; shell is opaque glassy clearcoat on top.
  liquid.mesh.renderOrder = Math.min(liquid.mesh.renderOrder || 0, 0);
  glass.mesh.renderOrder = Math.max(glass.mesh.renderOrder || 0, 1);
  applyTransmissionAttenuation(liquid.mat, {
    meshName: liquid.name,
    parentName: liquid.mesh.parent?.name,
  });
}

/**
 * Classic full glass enclosure: glass keeps Transmit, liquid becomes wet opaque.
 */
function keepGlassDemoteLiquid(liquid: TxEntry, glass: TxEntry, demoted: Set<string>): void {
  if (demoted.has(liquid.mesh.name || liquid.mesh.uuid)) return;
  applyWetOpaqueLiquid(liquid.mat, liquid.mat.transmission);
  softenTransmittingGlass(glass.mat);
  markDemoted(liquid.mesh, demoted);
  liquid.mesh.userData.c3dNestedGlassOuter = false;
  glass.mesh.userData.c3dNestedGlassOuter = true;
  glass.mesh.userData.c3dNestedTransmissionDemoted = false;
  liquid.mesh.renderOrder = Math.min(liquid.mesh.renderOrder || 0, 0);
  glass.mesh.renderOrder = Math.max(glass.mesh.renderOrder || 0, 1);
}

/**
 * Three.js only supports one transmission layer. Strategy:
 *  - Named/neck glass (smaller) + larger liquid → **keep liquid Transmit**, demote glass
 *  - Full glass shell enclosing liquid → **keep glass Transmit**, demote liquid to wet opaque
 *
 * Call on the full scene root (glass/liquid are often separate objects).
 *
 * Returns mesh names whose transmission was removed (skip re-applying graph transmission).
 */
export function demoteNestedTransmission(root: THREE.Object3D): Set<string> {
  const demoted = new Set<string>();

  // Fast path: already resolved nesting — only re-apply shell softens / keep demoted zero-tx.
  let activeTx = 0;
  let revivedDemoted = false;
  let nestedShells = 0;
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const mat = obj.material;
    if (!(mat instanceof THREE.MeshPhysicalMaterial)) return;
    if (Array.isArray(obj.material)) return;
    const tx = mat.transmission ?? 0;
    if (tx > 0.001) {
      activeTx++;
      if (obj.userData.c3dNestedTransmissionDemoted) revivedDemoted = true;
    } else if (obj.userData.c3dNestedTransmissionDemoted) {
      nestedShells++;
      // Graph may have restored roughness etc.; keep demoted mats non-transmissive.
      if (obj.userData.c3dNestedGlassOuter) {
        softenTransmittingGlass(mat);
      }
    }
    if (obj.userData.c3dNestedGlassOuter && tx > 0.001) {
      nestedShells++;
      softenTransmittingGlass(mat);
    }
  });
  if (nestedShells > 0 && activeTx <= 1 && !revivedDemoted) {
    return demoted;
  }

  const entries: TxEntry[] = [];
  root.updateMatrixWorld(true);
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const mat = obj.material;
    if (!(mat instanceof THREE.MeshPhysicalMaterial) || !(mat.transmission > 0)) return;
    if (Array.isArray(obj.material)) return;
    const box = new THREE.Box3().setFromObject(obj);
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3()).length();
    entries.push({
      mesh: obj,
      mat,
      box,
      size,
      vol: boxVolume(box),
      name: obj.name || '',
    });
  });

  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const mat = obj.material;
    if (!(mat instanceof THREE.MeshPhysicalMaterial) || !(mat.transmission > 0)) return;
    if ((mat.opacity ?? 1) < 0.999) {
      mat.opacity = 1;
      mat.transparent = false;
      mat.depthWrite = true;
      mat.needsUpdate = true;
    }
  });

  if (entries.length < 2) return demoted;

  const paired = new Set<string>();

  // Pass 0: explicit Glass + Liquid names — prefer geometry style (neck vs full shell).
  for (let i = 0; i < entries.length; i++) {
    for (let j = i + 1; j < entries.length; j++) {
      const a = entries[i]!;
      const b = entries[j]!;
      const aLiq = nameHintsLiquid(a.name) || nameHintsLiquid(a.mesh.parent?.name || '');
      const bLiq = nameHintsLiquid(b.name) || nameHintsLiquid(b.mesh.parent?.name || '');
      const aGlass = nameHintsGlass(a.name) || nameHintsGlass(a.mesh.parent?.name || '');
      const bGlass = nameHintsGlass(b.name) || nameHintsGlass(b.mesh.parent?.name || '');
      if (!((aLiq && bGlass) || (bLiq && aGlass))) continue;
      const { liquid, glass } = classifyPair(a, b);
      // Full glass shell around liquid (Paradis body) → glass Transmit, wet liquid.
      // Small neck glass + large liquid → liquid Transmit.
      if (isNeckStyleGlass(liquid, glass)) {
        keepLiquidDemoteGlass(liquid, glass, demoted);
      } else {
        keepGlassDemoteLiquid(liquid, glass, demoted);
      }
      paired.add(a.mesh.uuid);
      paired.add(b.mesh.uuid);
    }
  }

  // Pass 1: containment. Careful — a neck glass often sits *inside* the liquid AABB.
  for (let i = 0; i < entries.length; i++) {
    for (let j = 0; j < entries.length; j++) {
      if (i === j) continue;
      const inner = entries[i]!;
      const outer = entries[j]!;
      if (inner.size >= outer.size * 0.98) continue;
      if (!outer.box.containsBox(inner.box) && !boxMostlyInside(inner.box, outer.box, 0.75)) continue;

      const { liquid, glass } = classifyPair(inner, outer);
      if (isNeckStyleGlass(liquid, glass)) {
        // Smaller glass inside/near larger liquid body → keep liquid Transmit.
        keepLiquidDemoteGlass(liquid, glass, demoted);
      } else {
        // True enclosure: liquid inside larger glass shell.
        keepGlassDemoteLiquid(liquid, glass, demoted);
      }
      paired.add(inner.mesh.uuid);
      paired.add(outer.mesh.uuid);
    }
  }

  // Pass 2: overlapping transmitters, neck glass over large liquid → keep liquid Transmit.
  for (let i = 0; i < entries.length; i++) {
    for (let j = i + 1; j < entries.length; j++) {
      const a = entries[i]!;
      const b = entries[j]!;
      if (paired.has(a.mesh.uuid) && paired.has(b.mesh.uuid)) continue;
      const margin = Math.max(0.01, Math.min(a.size, b.size) * 0.1);
      if (!boxesNearOrIntersect(a.box, b.box, margin)) {
        const aLiq = nameHintsLiquid(a.name);
        const bLiq = nameHintsLiquid(b.name);
        const aGlass = nameHintsGlass(a.name);
        const bGlass = nameHintsGlass(b.name);
        if (!((aLiq && bGlass) || (bLiq && aGlass))) continue;
      }

      const { liquid, glass } = classifyPair(a, b);
      if (isNeckStyleGlass(liquid, glass)) {
        keepLiquidDemoteGlass(liquid, glass, demoted);
      } else {
        keepGlassDemoteLiquid(liquid, glass, demoted);
      }
      paired.add(a.mesh.uuid);
      paired.add(b.mesh.uuid);
    }
  }

  for (const e of entries) {
    if (!demoted.has(e.mesh.name || e.mesh.uuid)) {
      e.mesh.userData.c3dNestedTransmissionDemoted = false;
    }
  }

  // Never leave the bottle with zero transmitters (mis-classify → opaque shell).
  let alive = 0;
  for (const e of entries) {
    if ((e.mat.transmission ?? 0) > 0.001) alive++;
  }
  if (alive === 0 && entries.length > 0) {
    const preferGlass = entries.find((e) => nameHintsGlass(e.name) || nameHintsGlass(e.mesh.parent?.name || ''));
    const preferLiquid = entries.find((e) => nameHintsLiquid(e.name) || nameHintsLiquid(e.mesh.parent?.name || ''));
    // Full shell: restore glass. Neck-only scene: restore liquid.
    const revive = preferGlass ?? preferLiquid ?? [...entries].sort((a, b) => b.vol - a.vol)[0]!;
    revive.mat.transmission = 1;
    revive.mat.transparent = false;
    revive.mat.depthWrite = true;
    revive.mat.opacity = 1;
    revive.mat.needsUpdate = true;
    revive.mesh.userData.c3dNestedTransmissionDemoted = false;
    revive.mesh.userData.c3dNestedGlassOuter = !!preferGlass;
    demoted.delete(revive.mesh.name || revive.mesh.uuid);
  }

  return demoted;
}
